import React, { useCallback, useEffect, useState } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import { Link } from 'react-router-dom';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Database,
  FlaskConical,
  Gauge,
  History,
  RefreshCw,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { ClassicalReadingsCard, InterpretationPanel } from '../components/macro/MacroInterpretation';
import {
  MacroComponentEvidence,
  MacroComponentState,
  MacroDerivationMode,
  MacroEconomicState,
  MacroGrowthState,
  MacroMomentumDirection,
  MacroRegimeAxis,
  MacroRegimeDirection,
  MacroRegimeLabel,
  MacroRegimeState,
  MacroStateEvidenceResponse,
  MacroStateHistoryResponse,
  MacroTrackState,
  MacroTrendState,
} from '../types/macroData';

type RangeOption = '10y' | '25y' | 'max';
type TrackKey = 'real' | 'credit';
type TrackSummary = Pick<MacroTrackState, 'position' | 'momentum'>;

const RANGE_YEARS: Record<RangeOption, number | null> = { '10y': 10, '25y': 25, max: null };
const TRACK_TITLES: Record<TrackKey, string> = { real: 'Real economy', credit: 'Credit' };
const TRACK_COLORS: Record<TrackKey, string> = { real: '#2563eb', credit: '#16a34a' };

const fromDateFor = (range: RangeOption): string | undefined => {
  const years = RANGE_YEARS[range];
  if (years === null) return undefined;
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() - years);
  return date.toISOString().slice(0, 10);
};

// Level (growth sign), trend and momentum use different palettes on purpose:
// "below trend" must never look like "contracting".
const GROWTH_STYLES: Record<MacroGrowthState, string> = {
  expanding: 'bg-green-100 text-green-800',
  contracting: 'bg-red-100 text-red-800',
  mixed: 'bg-amber-100 text-amber-800',
  unknown: 'bg-gray-100 text-gray-600',
};
const TREND_LABELS: Record<MacroTrendState, string> = {
  above_trend: 'above trend',
  near_trend: 'near trend',
  below_trend: 'below trend',
  unknown: 'trend unknown',
};
const STATUS_STYLES: Record<MacroComponentState['status'], string> = {
  fresh: 'text-green-700',
  stale: 'text-amber-700',
  missing: 'text-gray-400',
};

const fmt = (value: number | null | undefined, digits = 2) =>
  value === null || value === undefined ? '—' : value.toFixed(digits);
const signed = (value: number | null | undefined, digits = 2) =>
  value === null || value === undefined ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(digits)}`;
const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;
const formatNumber = (value: number) =>
  Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
const formatDate = (value: string) => new Date(value).toLocaleDateString();

const momentumPhrase = (m: MacroTrackState['momentum']) =>
  m.direction === 'unknown'
    ? 'momentum unknown'
    : m.direction === 'mixed'
      ? 'momentum mixed'
      : `momentum ${m.direction} ${m.confirmed ? `(confirmed, ${m.monthsInDirection} mo)` : '(not yet confirmed)'}`;

const describeTrack = (t: TrackSummary) =>
  `${t.position.growthState === 'unknown' ? 'growth unknown' : t.position.growthState}, ${TREND_LABELS[t.position.trendState]}; ${momentumPhrase(t.momentum)}`;

/** Momentum that just turned, or confirmed momentum running against the position. */
const transitionNote = (t: TrackSummary): string | null => {
  const { direction, confirmed, monthsInDirection } = t.momentum;
  if ((direction === 'improving' || direction === 'deteriorating') && monthsInDirection === 1) {
    return `momentum turned ${direction} this month, not yet confirmed`;
  }
  if (confirmed && t.position.growthState === 'expanding' && direction === 'deteriorating') {
    return 'still expanding, but momentum has been deteriorating';
  }
  if (confirmed && t.position.growthState === 'contracting' && direction === 'improving') {
    return 'still contracting, but momentum has been improving';
  }
  return null;
};

const MomentumArrow: React.FC<{ direction: MacroMomentumDirection; className?: string }> = ({
  direction,
  className = 'h-5 w-5',
}) => {
  if (direction === 'improving') return <ArrowUpRight className={`${className} text-blue-600`} />;
  if (direction === 'deteriorating') return <ArrowDownRight className={`${className} text-orange-600`} />;
  return <ArrowRight className={`${className} text-gray-400`} />;
};

const PositionBadges: React.FC<{ position: MacroTrackState['position'] }> = ({ position }) => (
  <>
    <span className={`rounded px-2 py-0.5 text-sm font-medium ${GROWTH_STYLES[position.growthState]}`}>
      {position.growthState}
    </span>
    <span className="rounded border border-slate-300 px-2 py-0.5 text-sm text-slate-700">
      {TREND_LABELS[position.trendState]}
    </span>
  </>
);

const TrackCard: React.FC<{ track: TrackKey; state: MacroTrackState; previous?: TrackSummary }> = ({
  track,
  state,
  previous,
}) => {
  const { position, momentum, sufficiency } = state;
  const changed =
    previous &&
    (previous.position.growthState !== position.growthState ||
      previous.position.trendState !== position.trendState ||
      previous.momentum.direction !== momentum.direction);
  const note = transitionNote(state);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{TRACK_TITLES[track]}</CardTitle>
          <span className="text-xs text-gray-500">
            {sufficiency.fresh}/{sufficiency.total} components current · {sufficiency.confidence} confidence
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Position</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <PositionBadges position={position} />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Composite z {fmt(position.compositeZ)} · {pct(position.growingShare)} of components growing
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Momentum</div>
            <div className="mt-1 flex items-center gap-2 text-sm font-medium text-gray-900">
              <MomentumArrow direction={momentum.direction} />
              {momentum.direction}
              <span className="text-xs font-normal text-gray-500">
                {momentum.confirmed ? 'confirmed' : 'unconfirmed'} · {momentum.monthsInDirection} mo
              </span>
            </div>
            <div className="mt-2 text-xs text-gray-500">{pct(momentum.improvingShare)} of components improving</div>
          </div>
        </div>
        {(changed || note) && (
          <div className="mt-3 space-y-1 border-t border-gray-100 pt-2 text-xs text-gray-600">
            {changed && <div>Previous month: {describeTrack(previous)}</div>}
            {note && <div className="text-purple-700">Watch: {note}</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ReadingSummary: React.FC<{
  state: MacroEconomicState;
  previous: MacroStateEvidenceResponse['previous'];
  evidence: MacroComponentEvidence[];
}> = ({ state, previous, evidence }) => {
  const names = new Map(evidence.map((e) => [e.seriesKey, e.name]));
  const components = [...(state.real.components ?? []), ...(state.credit.components ?? [])];
  const prevContribution = new Map(evidence.map((e) => [e.seriesKey, e.previousContribution]));
  const movers = components
    .map((c) => ({ c, delta: (c.contribution ?? 0) - (prevContribution.get(c.seriesKey) ?? 0) }))
    .filter((m) => Math.abs(m.delta) >= 0.01)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 3);
  const stale = components.filter((c) => c.status === 'stale');
  const missing = components.filter((c) => c.status === 'missing');
  const fresh = components.length - stale.length - missing.length;

  const { real, credit, divergence } = state;
  const together =
    divergence.momentumOpposed
      ? `Diverging: real momentum ${real.momentum.direction} while credit momentum ${credit.momentum.direction} (both confirmed).`
      : real.momentum.confirmed &&
          credit.momentum.confirmed &&
          real.momentum.direction === credit.momentum.direction &&
          real.momentum.direction !== 'mixed'
        ? `Moving together: both ${real.momentum.direction}.`
        : 'No clear joint direction.';
  const gap =
    divergence.value === null
      ? ''
      : ` Credit composite is ${Math.abs(divergence.value).toFixed(2)} z ${divergence.value >= 0 ? 'above' : 'below'} real activity.`;
  const transitions = (['real', 'credit'] as TrackKey[])
    .map((t) => ({ t, note: transitionNote(state[t]) }))
    .filter((x) => x.note);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reading for end of {state.asOfLabel}{state.provisional && ' (month in progress)'}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm md:grid-cols-[max-content_1fr]">
          <dt className="text-gray-500">Real economy</dt>
          <dd className="text-gray-900">{describeTrack(real)}</dd>
          <dt className="text-gray-500">Credit</dt>
          <dd className="text-gray-900">{describeTrack(credit)}</dd>
          <dt className="text-gray-500">Together or diverging</dt>
          <dd className="text-gray-900">
            {together}
            {gap}
          </dd>
          <dt className="text-gray-500">What changed{previous ? ` since ${previous.asOfLabel}` : ''}</dt>
          <dd className="text-gray-900">
            {movers.length
              ? movers
                  .map((m) => `${names.get(m.c.seriesKey) ?? m.c.seriesKey} ${signed(m.delta)}`)
                  .join(' · ')
              : 'No component moved its track by 0.01 z or more.'}
          </dd>
          <dt className="text-gray-500">Possible transition</dt>
          <dd className="text-gray-900">
            {transitions.length
              ? transitions.map((x) => `${TRACK_TITLES[x.t]}: ${x.note}`).join('. ')
              : 'None flagged.'}{' '}
            <span className="text-gray-500">Descriptive only, not a forecast.</span>
          </dd>
          <dt className="text-gray-500">Data basis</dt>
          <dd className="text-gray-900">
            {fresh} of {components.length} components current
            {stale.length > 0 && `; stale: ${stale.map((c) => names.get(c.seriesKey)).join(', ')}`}
            {missing.length > 0 && `; missing: ${missing.map((c) => names.get(c.seriesKey)).join(', ')}`}. Stale and
            missing components are excluded. Every reading is a published observation; nothing is nowcast.
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
};

const EvidenceTable: React.FC<{
  track: TrackKey;
  components: MacroComponentState[];
  evidence: Map<string, MacroComponentEvidence>;
}> = ({ track, components, evidence }) => (
  <div className="overflow-x-auto">
    <h3 className="mb-1 text-sm font-semibold text-gray-800">{TRACK_TITLES[track]}</h3>
    <table className="min-w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="py-1 pr-3">Component</th>
          <th className="py-1 pr-3">Status</th>
          <th className="py-1 pr-3">Latest observation</th>
          <th className="py-1 pr-3">Released</th>
          <th className="py-1 pr-3">Revisions</th>
          <th className="py-1 pr-3">Growth</th>
          <th className="py-1 pr-3" title="Signed: positive is good for the track">z</th>
          <th className="py-1 pr-3" title="Signed: positive is improving">Momentum</th>
          <th className="py-1 pr-3" title="Share of the track composite; contributions sum to it">Contribution</th>
          <th className="py-1">Change</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {components.map((c) => {
          const e = evidence.get(c.seriesKey);
          const obs = e?.observation;
          const delta =
            c.contribution !== null && e?.previousContribution !== null && e?.previousContribution !== undefined
              ? c.contribution - e.previousContribution
              : null;
          return (
            <tr key={c.seriesKey} className={c.status === 'fresh' ? 'align-top' : 'align-top text-gray-400'}>
              <td className="py-1.5 pr-3">
                <div className="font-medium text-gray-900">{e?.name ?? c.seriesKey}</div>
                <div className="text-[11px] text-gray-500">
                  {e?.source} · {e?.frequency} · {e?.leadLagTag}
                  {c.sign === -1 && ' · inverted (higher is worse)'}
                </div>
              </td>
              <td className={`py-1.5 pr-3 font-medium ${STATUS_STYLES[c.status]}`}>
                {c.status === 'fresh' ? 'current' : c.status}
              </td>
              <td className="py-1.5 pr-3 whitespace-nowrap">
                {obs ? (
                  <>
                    <div className="text-gray-900">
                      {formatNumber(obs.value)}
                      {obs.status === 'preliminary' && <span className="text-gray-500"> (prelim)</span>}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {obs.periodLabel} · {e.unit}
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className="py-1.5 pr-3 whitespace-nowrap">
                {obs ? (
                  obs.fromInitialLoad ? (
                    <span title="Loaded with history on Marlin's first run; the agency's publication date was not recorded">
                      before Marlin
                    </span>
                  ) : (
                    formatDate(obs.firstReleaseTime)
                  )
                ) : (
                  '—'
                )}
              </td>
              <td className="py-1.5 pr-3">
                {obs ? (obs.revisionCount ? `${obs.revisionCount} (last ${formatDate(obs.releaseTime)})` : '0') : '—'}
              </td>
              <td className="py-1.5 pr-3">{c.growth === null ? '—' : `${fmt(c.growth, 1)}%`}</td>
              <td className="py-1.5 pr-3">{signed(c.score)}</td>
              <td className="py-1.5 pr-3">{signed(c.momentum)}</td>
              <td className="py-1.5 pr-3">{signed(c.contribution)}</td>
              <td className={`py-1.5 ${delta === null ? '' : delta > 0 ? 'text-green-700' : delta < 0 ? 'text-red-700' : ''}`}>
                {signed(delta)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const REGIME_TITLES: Record<MacroRegimeLabel, string> = {
  reflationary_expansion: 'Reflationary expansion',
  disinflationary_expansion: 'Disinflationary expansion',
  disinflationary_slowdown: 'Disinflationary slowdown',
  stagflationary: 'Stagflationary',
  mixed: 'Mixed (an axis is near its baseline)',
  unknown: 'Unknown (an axis has no current reading)',
};
const REGIME_QUADRANTS: Array<{ label: MacroRegimeLabel; growth: string; inflation: string; style: string }> = [
  { label: 'stagflationary', growth: 'below', inflation: 'above', style: 'bg-red-50 border-red-300 text-red-900' },
  { label: 'reflationary_expansion', growth: 'above', inflation: 'above', style: 'bg-amber-50 border-amber-300 text-amber-900' },
  { label: 'disinflationary_slowdown', growth: 'below', inflation: 'below', style: 'bg-sky-50 border-sky-300 text-sky-900' },
  { label: 'disinflationary_expansion', growth: 'above', inflation: 'below', style: 'bg-green-50 border-green-300 text-green-900' },
];
const REGIME_SERIES_NAMES: Record<string, string> = {
  spf_rgdp_growth: 'Real GDP growth',
  spf_core_pce: 'Core PCE inflation',
  spf_cpi: 'CPI inflation',
  core_pce_price_index: 'Core PCE',
  cpi: 'CPI',
  core_cpi: 'Core CPI',
};
const DIRECTION_TEXT: Record<MacroRegimeDirection, string> = {
  upside: 'above baseline',
  downside: 'below baseline',
  neutral: 'near baseline',
  unknown: 'unknown',
};

const RegimeAxisDetail: React.FC<{ title: string; axis: MacroRegimeAxis }> = ({ title, axis }) => {
  const name = axis.seriesKey ? REGIME_SERIES_NAMES[axis.seriesKey] ?? axis.seriesKey : null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
      <div className="mt-1 text-sm font-medium text-gray-900">
        {DIRECTION_TEXT[axis.direction]}
        {axis.score !== null && (
          <span className="font-normal text-gray-600">
            {' '}
            · {signed(axis.score)} pp ({axis.periodLabel}, 2-quarter average; band ±{axis.neutralBand})
          </span>
        )}
      </div>
      {axis.score === null ? (
        <div className="mt-1 text-xs text-gray-500">No reading for this period.</div>
      ) : (
        <div className="mt-1 space-y-0.5 text-xs text-gray-600">
          <div>
            {name},{' '}
            {axis.basis === 'survey'
              ? 'measured against the Survey of Professional Forecasters'
              : 'measured against its own trailing 12-quarter average (no survey for this period)'}
            .
          </div>
          {axis.realized && (
            <div>
              Realized {fmt(axis.realized.value, 1)}% ({axis.realized.periodLabel}, q/q annualized)
              {axis.surprise && <> · surprise {signed(axis.surprise.value, 1)} pp that quarter</>}
            </div>
          )}
          {axis.expected && (
            <div>
              Forecasters expect {fmt(axis.expected.value, 1)}% for {axis.expected.periodLabel}
            </div>
          )}
          {axis.stale && <div className="text-amber-700">Stale: the next quarter is overdue, so it does not vote.</div>}
        </div>
      )}
    </div>
  );
};

const RegimeCard: React.FC<{ regime?: MacroRegimeState; previous?: MacroRegimeLabel }> = ({ regime, previous }) => (
  <Card>
    <CardHeader>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Growth and inflation regime</CardTitle>
        <span className="text-xs text-gray-500">Overlay, separate from the cycle tracks</span>
      </div>
    </CardHeader>
    <CardContent>
      {!regime ? (
        <p className="text-sm text-gray-500">No regime reading stored for this month yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,18rem)_1fr]">
          <div>
            <div className="grid grid-cols-[auto_1fr_1fr] gap-1 text-[11px]">
              <div />
              <div className="text-center text-gray-500">growth below</div>
              <div className="text-center text-gray-500">growth above</div>
              {[0, 2].map((row) => (
                <React.Fragment key={row}>
                  <div className="flex items-center pr-1 text-right text-gray-500">
                    inflation {row === 0 ? 'above' : 'below'}
                  </div>
                  {REGIME_QUADRANTS.slice(row, row + 2).map((q) => (
                    <div
                      key={q.label}
                      className={`flex h-16 items-center justify-center rounded border p-1 text-center ${
                        regime.label === q.label ? `${q.style} border-2 font-semibold` : 'border-gray-200 text-gray-400'
                      }`}
                    >
                      {REGIME_TITLES[q.label]}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="mt-2 text-sm font-medium text-gray-900">{REGIME_TITLES[regime.label]}</div>
            {previous && previous !== regime.label && (
              <div className="text-xs text-gray-500">Previous month: {REGIME_TITLES[previous]}</div>
            )}
          </div>
          <div className="space-y-4">
            <RegimeAxisDetail title="Growth surprise" axis={regime.growth} />
            <RegimeAxisDetail title="Inflation surprise" axis={regime.inflation} />
            {!!regime.inflationContext?.length && (
              <div className="text-xs text-gray-600">
                Realized inflation, year over year:{' '}
                {regime.inflationContext
                  .map((c) => `${REGIME_SERIES_NAMES[c.seriesKey] ?? c.seriesKey} ${fmt(c.value, 1)}% (${c.periodLabel})`)
                  .join(' · ')}
              </div>
            )}
            <p className="text-[11px] text-gray-400">
              Regimes are read from surprises (what happened versus what was expected), not from levels: high but
              expected inflation is not an upside surprise. Bands are unvalidated defaults.
            </p>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

interface ChartRow {
  label: string;
  real: number | null;
  credit: number | null;
  divergence: number | null;
  opposed: boolean;
  turned: TrackKey | null;
  growthSurprise: number | null;
  inflationSurprise: number | null;
  regime: MacroRegimeLabel | null;
}

const FlagDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (cx === undefined || cy === undefined || cy === null) return <g />;
  if (payload.opposed) return <circle cx={cx} cy={cy} r={3.5} fill="#9333ea" />;
  if (payload.turned) return <circle cx={cx} cy={cy} r={2.5} fill={TRACK_COLORS[payload.turned as TrackKey]} />;
  return <g />;
};

const MacroCycle: React.FC = () => {
  const [mode, setMode] = useState<MacroDerivationMode>('latest_revised');
  const [range, setRange] = useState<RangeOption>('25y');
  const [asOf, setAsOf] = useState<string | null>(null);
  const [history, setHistory] = useState<MacroStateHistoryResponse | null>(null);
  const [detail, setDetail] = useState<MacroStateEvidenceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHistory(await apiService.getMacroStateHistory({ mode, from: fromDateFor(range) }));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load cycle history');
    } finally {
      setLoading(false);
    }
  }, [mode, range]);

  const loadDetail = useCallback(async () => {
    setDetailLoading(true);
    setError(null);
    try {
      setDetail(await apiService.getMacroStateEvidence({ mode, asOf: asOf ?? undefined }));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load cycle state');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [mode, asOf]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const states = history?.states ?? [];
  const chartData: ChartRow[] = states.map((s) => ({
    label: s.asOfLabel,
    real: s.real.position.compositeZ,
    credit: s.credit.position.compositeZ,
    divergence: s.divergence.value,
    opposed: s.divergence.momentumOpposed,
    turned: s.divergence.turnedTrack,
    growthSurprise: s.regime && !s.regime.growth.stale ? s.regime.growth.score : null,
    inflationSurprise: s.regime && !s.regime.inflation.stale ? s.regime.inflation.score : null,
    regime: s.regime?.label ?? null,
  }));
  const previousRegime = (() => {
    const i = states.findIndex((s) => s.asOfLabel === detail?.state?.asOfLabel);
    return i > 0 ? states[i - 1].regime?.label : undefined;
  })();
  const firstLabel = chartData[0]?.label;
  const lastLabel = chartData[chartData.length - 1]?.label;
  const recessions = (history?.referenceRecessions ?? []).filter(
    (r) => firstLabel && lastLabel && r.trough >= firstLabel && r.peak <= lastLabel,
  );
  const state = detail?.state;
  const selectedLabel = state?.asOfLabel;
  const evidenceByKey = new Map((detail?.evidence ?? []).map((e) => [e.seriesKey, e]));
  const selectFromChart = (e: any) => e?.activeLabel && setAsOf(e.activeLabel);

  const recessionShading = recessions.map((r) => (
    <ReferenceArea
      key={r.peak}
      x1={r.peak < firstLabel ? firstLabel : r.peak}
      x2={r.trough > lastLabel ? lastLabel : r.trough}
      fill="#e5e7eb"
      fillOpacity={0.6}
      ifOverflow="hidden"
    />
  ));
  const selectedMarker = selectedLabel && selectedLabel >= firstLabel && selectedLabel <= lastLabel && (
    <ReferenceLine x={selectedLabel} stroke="#111827" strokeDasharray="2 2" />
  );

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Cycle data · Tier 1"
        description="Long-horizon macro cycle inputs drive Tier 1 macro ETF allocation rules."
        primaryTier={1}
        thesisTimeframe="6_months_plus"
      />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marlin Cycle Monitor</h1>
          <p className="text-sm text-gray-500">
            Real-economy and credit tracks on one timeline: position and momentum shown separately.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-sm text-gray-600">
            <History className="h-4 w-4" /> As of
            <input
              type="month"
              value={selectedLabel ?? ''}
              min={firstLabel}
              max={lastLabel}
              onChange={(e) => e.target.value && setAsOf(e.target.value)}
              className="rounded border-gray-300 py-1 text-sm"
            />
          </label>
          {asOf && (
            <button
              onClick={() => setAsOf(null)}
              className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm hover:bg-gray-50"
            >
              Latest
            </button>
          )}
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value as MacroDerivationMode);
              setAsOf(null);
            }}
            className="rounded border-gray-300 text-sm"
          >
            <option value="latest_revised">Latest revised</option>
            <option value="point_in_time">Point in time</option>
          </select>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as RangeOption)}
            className="rounded border-gray-300 text-sm"
          >
            <option value="10y">10 years</option>
            <option value="25y">25 years</option>
            <option value="max">Max</option>
          </select>
          <button
            onClick={() => {
              loadHistory();
              loadDetail();
            }}
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Reload
          </button>
          <Link
            to="/app/macro"
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <Gauge className="h-4 w-4" /> Cockpit
          </Link>
          <Link
            to="/app/macro/data"
            className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <Database className="h-4 w-4" /> Data
          </Link>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <FlaskConical className="h-5 w-5 flex-shrink-0" />
        <span>
          Research only and isolated from trading. Thresholds are unvalidated defaults, not calibrated signals.{' '}
          {mode === 'latest_revised'
            ? "Latest revised replays each month with today's revised numbers, limited to periods that would have been published by then."
            : 'Point in time uses only what Marlin had stored at each month-end, so it starts in October 2026.'}{' '}
          Pick a month or click a chart to replay it.
        </span>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading || (detailLoading && !state) ? (
        <LoadingSpinner />
      ) : !state ? (
        <Card>
          <CardContent>
            <p className="py-6 text-center text-sm text-gray-500">
              No cycle states stored for this mode yet. They are generated after the next ingestion run.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className={detailLoading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {(['real', 'credit'] as TrackKey[]).map((t) => (
                <TrackCard key={t} track={t} state={state[t]} previous={detail.previous?.[t]} />
              ))}
            </div>
          </div>

          <ReadingSummary state={state} previous={detail.previous} evidence={detail.evidence} />

          <RegimeCard regime={state.regime} previous={previousRegime} />

          <ClassicalReadingsCard frameworks={state.frameworks} />

          <InterpretationPanel mode={mode} asOfLabel={state.asOfLabel} />

          <Card>
            <CardHeader>
              <CardTitle>Track composites (z)</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 1 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} onClick={selectFromChart} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={40} />
                      <YAxis tick={{ fontSize: 10 }} width={40} domain={['auto', 'auto']} />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {recessionShading}
                      <ReferenceLine y={0} stroke="#9ca3af" />
                      {selectedMarker}
                      <Line type="monotone" dataKey="real" name="Real economy" stroke={TRACK_COLORS.real} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                      <Line type="monotone" dataKey="credit" name="Credit" stroke={TRACK_COLORS.credit} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-gray-500">
                  {mode === 'point_in_time'
                    ? 'Point-in-time history builds up one month at a time from October 2026.'
                    : 'Not enough history in this range.'}
                </p>
              )}
              <p className="mt-2 text-[11px] text-gray-400">
                Shaded: NBER recessions (reference only, not a model input). Composite = mean of each track's signed
                z-scores over current components. Click a month to replay it.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Credit vs real activity divergence</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 1 && (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} onClick={selectFromChart} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={40} />
                      <YAxis tick={{ fontSize: 10 }} width={40} domain={['auto', 'auto']} />
                      <Tooltip
                        formatter={(v: number) => fmt(v)}
                        labelFormatter={(label: string, payload: any[]) => {
                          const row: ChartRow | undefined = payload?.[0]?.payload;
                          const flags = [
                            row?.opposed && 'momentum opposed',
                            row?.turned && `${TRACK_TITLES[row.turned]} momentum turned`,
                          ].filter(Boolean);
                          return flags.length ? `${label} (${flags.join(', ')})` : label;
                        }}
                      />
                      {recessionShading}
                      <ReferenceLine y={0} stroke="#9ca3af" />
                      {selectedMarker}
                      <Line type="monotone" dataKey="divergence" name="Credit − real (z)" stroke="#6b7280" dot={<FlagDot />} activeDot={{ r: 4 }} strokeWidth={1.25} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-gray-500">
                <span>Line: credit composite minus real composite (above 0 = credit stronger).</span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-purple-600" /> both momenta confirmed in opposite directions
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-blue-600" /> real momentum turned
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-600" /> credit momentum turned
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Growth and inflation surprises (pp, 2-quarter average)</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 1 && (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} onClick={selectFromChart} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={40} />
                      <YAxis tick={{ fontSize: 10 }} width={40} domain={['auto', 'auto']} />
                      <Tooltip
                        formatter={(v: number) => signed(v)}
                        labelFormatter={(label: string, payload: any[]) => {
                          const row: ChartRow | undefined = payload?.[0]?.payload;
                          return row?.regime ? `${label} · ${REGIME_TITLES[row.regime]}` : label;
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {recessionShading}
                      <ReferenceLine y={0} stroke="#9ca3af" />
                      {selectedMarker}
                      <Line type="stepAfter" dataKey="growthSurprise" name="Growth surprise" stroke="#2563eb" dot={false} strokeWidth={1.5} isAnimationActive={false} connectNulls={false} />
                      <Line type="stepAfter" dataKey="inflationSurprise" name="Inflation surprise" stroke="#dc2626" dot={false} strokeWidth={1.5} isAnimationActive={false} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <p className="mt-2 text-[11px] text-gray-400">
                Realized minus baseline, averaged over two quarters. Baseline is the Survey of Professional Forecasters
                where it exists (growth from 1968, CPI from 1981, core PCE from 2007), otherwise the trailing 12-quarter
                average. Neutral bands: growth ±0.5 pp, inflation ±0.25 pp. Gaps are stale readings.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Evidence and components · end of {state.asOfLabel}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {(['real', 'credit'] as TrackKey[]).map((t) => (
                <EvidenceTable key={t} track={t} components={state[t].components ?? []} evidence={evidenceByKey} />
              ))}
              <p className="text-[11px] text-gray-400">
                {mode === 'point_in_time'
                  ? 'Observations and revisions as Marlin had them at this month-end.'
                  : "Observations at today's revised values."}{' '}
                "Before Marlin" means the period was loaded with history on the first run, so its agency publication date
                wasn't recorded. Change = contribution change since {detail.previous?.asOfLabel ?? 'the previous month'}.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MacroCycle;
